/**
 * Utility functions for handling timezone conversion from UTC to local timezone
 */

/**
 * Convert UTC datetime string to local Date object
 * @param {string} datetime - UTC datetime string from backend
 * @returns {Date} - Date object in local timezone
 */
export const parseUTCDatetime = (datetime) => {
  if (!datetime) return null;
  // Add 'Z' to ensure UTC parsing if not already present
  const utcString = datetime.endsWith('Z') ? datetime : datetime + 'Z';
  return new Date(utcString);
};

/**
 * Get relative time (e.g., "2 hours ago") from UTC datetime
 * @param {string} datetime - UTC datetime string from backend
 * @returns {string} - Relative time in Persian
 */
export const getRelativeTime = (datetime) => {
  if (!datetime) return '';
  
  const utcDate = parseUTCDatetime(datetime);
  if (!utcDate) return '';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - utcDate) / 1000);

  const formatter = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
  
  if (diffInSeconds < 60) {
    return formatter.format(-diffInSeconds, "second");
  } else if (diffInSeconds < 3600) {
    return formatter.format(-Math.floor(diffInSeconds / 60), "minute");
  } else if (diffInSeconds < 86400) {
    return formatter.format(-Math.floor(diffInSeconds / 3600), "hour");
  } else if (diffInSeconds < 2592000) {
    return formatter.format(-Math.floor(diffInSeconds / 86400), "day");
  } else if (diffInSeconds < 31536000) {
    return formatter.format(-Math.floor(diffInSeconds / 2592000), "month");
  } else {
    return formatter.format(-Math.floor(diffInSeconds / 31536000), "year");
  }
};

/**
 * Format datetime to readable string in browser's local timezone
 * @param {string} datetime - UTC datetime string from backend
 * @returns {string} - Formatted datetime in Persian
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return '';
  
  const utcDate = parseUTCDatetime(datetime);
  if (!utcDate) return '';
  
  return new Intl.DateTimeFormat('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
    // Removed timeZone: 'Asia/Tehran' to use browser's local timezone
  }).format(utcDate);
};

/**
 * Get interval between two dates (for admin table)
 * @param {string} datetime - UTC datetime string from backend
 * @returns {string} - Interval in Persian
 */
export const getInterval = (datetime) => {
  if (!datetime) return '';
  
  const now = new Date();
  const utcDate = parseUTCDatetime(datetime);
  if (!utcDate) return '';
  
  const diff = utcDate - now;
  const formatter = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
  return formatter.format(Math.round(diff / 86400000), "day");
}; 